// ============================================================
// A TOKEN'S LOOKS, DRAWN (plan body-map M1; engine/display/looks; the
// stacking rulings of 2026-10-09). Two layers the marker places: UNDER, after
// its ground (shadows, ground bands, pulses, the figure's own outlines and
// afterimages), and OVER, after its head (the head's light, rays, halo,
// window; the conduit's motes). Each look draws in the design's order, in the
// slot lookSlots gives it; past three, all are quieter and slower until the
// token is looked at. Seeded by the token's id, so every screen draws the
// same; animated in CSS only, so motion off holds them still.
// ============================================================

import { Fragment, useId, type CSSProperties, type ReactNode } from 'react';
import { lookQuiet, lookSlots, type LookSlots, type MapLook } from '../../engine/display/looks';
import lk from '../../styles/DisplayLooks.module.css';
import {
  ConduitMotes,
  DaemonEchoes,
  FearOutlines,
  HeadMark,
  PsykerHead,
  SwiftGhosts,
  VoidHead,
} from './looks/FigureLooks';
import {
  ConduitPool,
  DaemonShadow,
  Embers,
  FieldBand,
  NightShadow,
  ToxicPulse,
  UndyingPulse,
  RegenClimb,
  RuneBand,
  ScaledRing,
  VoidRing,
} from './looks/GroundLooks';
import type { LookPlace } from './looks/place';

/** What every look draws from. */
interface Drawn {
  id: string;
  /** Unique to this drawing of the layer: its clip ids, which two maps on one page must not share. */
  uid: string;
  p: LookPlace;
  slots: LookSlots;
  has: ReadonlySet<MapLook>;
}

type Layer = Partial<Record<MapLook, (d: Drawn) => ReactNode>>;

/** Under the figure: each look's shadow, ground band, pulse, outline or afterimage. */
const UNDER: Layer = {
  swift: (d) => <SwiftGhosts p={d.p} />,
  // The nightmare's moving shadow takes the daemon's wrong way too: one shadow, not two.
  daemonic: (d) => !d.has.has('night') && <DaemonShadow p={d.p} />,
  night: (d) => <NightShadow p={d.p} seed={d.id} toLight={d.has.has('daemonic')} />,
  field: (d) => <FieldBand p={d.p} radii={d.slots.bands.field!} />,
  psyker: (d) => (
    <>
      <RuneBand p={d.p} r={d.slots.bands.psyker![0]} seed={d.id} />
      <Embers p={d.p} />
    </>
  ),
  conduit: (d) => <ConduitPool p={d.p} r={d.slots.bands.conduit![0]} />,
  regen: (d) => <RegenClimb p={d.p} />,
  toxic: (d) => <ToxicPulse p={d.p} pulse0={d.slots.pulse0} />,
  undying: (d) => <UndyingPulse p={d.p} pulse0={d.slots.pulse0} />,
  fear: (d) => <FearOutlines p={d.p} />,
  beyond: (d) => <VoidRing p={d.p} seed={d.id} id={d.uid} />,
  natarm: (d) => <ScaledRing p={d.p} />,
};

/** Over the figure: what its head wears, and the conduit pouring out of it. */
const OVER: Layer = {
  daemonic: (d) => <DaemonEchoes p={d.p} />,
  psyker: (d) => <PsykerHead p={d.p} seed={d.id} />,
  conduit: (d) => <ConduitMotes p={d.p} />,
  toxic: (d) => <HeadMark p={d.p} className={lk.toxicFlash} />,
  beyond: (d) => <VoidHead p={d.p} seed={d.id} id={d.uid} />,
  frenzied: (d) => <HeadMark p={d.p} className={lk.halo} grow={2} />,
};

interface Props {
  id: string;
  looks: readonly MapLook[];
  place: LookPlace;
  /** Looked at - hovered, or the actor or the target: every look at full. */
  focused: boolean;
}

/** One layer of the looks, in the drawing order, at the loudness the crowd allows. */
function LookLayer({ id, looks, place, focused, layer, side }: Props & { layer: Layer; side: 'under' | 'over' }) {
  const uid = `${id}-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const shown = looks.filter((k) => layer[k]);
  if (!shown.length) return null;
  const { opacity, pace } = lookQuiet(looks.length, focused);
  const drawn: Drawn = { id, uid, p: place, slots: lookSlots(looks), has: new Set(looks) };
  return (
    <g
      className={lk.looks}
      data-looks={side}
      data-testid={`token-look-${side}-${id}`}
      style={{ opacity, '--look-pace': pace } as CSSProperties}
    >
      {shown.map((k) => (
        <Fragment key={k}>{layer[k]!(drawn)}</Fragment>
      ))}
    </g>
  );
}

export const LooksUnder = (p: Props) => <LookLayer {...p} layer={UNDER} side="under" />;
export const LooksOver = (p: Props) => <LookLayer {...p} layer={OVER} side="over" />;
